/**
 * AxLabs Physics Experiments - Optics
 *
 * Collection of optics experiments covering refraction, reflection,
 * lenses, mirrors, interference, diffraction, and polarization.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// ============================================================================
// SNELL'S LAW REFRACTION (Ray Tracing)
// ============================================================================
export const SnellsLawRefraction = createExperimentFromSchema({
    id: 'snells-law-refraction',
    name: "Snell's Law Refraction",
    domain: 'physics',
    description: 'Light ray refraction at interface between two media with ray tracing',
    conceptTags: ['Refraction', 'Snell\'s Law', 'Ray Optics', 'Index of Refraction'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Ray X Position', unit: 'm', initial: 0, symbol: 'x', description: 'Horizontal position of ray' },
        { id: 'y', name: 'Ray Y Position', unit: 'm', initial: -0.05, symbol: 'y', description: 'Vertical position of ray (negative = before interface)' },
        { id: 'theta', name: 'Ray Angle', unit: 'rad', initial: 0.3, symbol: '\\theta', description: 'Angle of incidence' },
    ],
    parameters: [
        { id: 'n1', name: 'Refractive Index 1', unit: '', min: 1, max: 2.5, default: 1, symbol: 'n_1', description: 'Index of refraction of first medium' },
        { id: 'n2', name: 'Refractive Index 2', unit: '', min: 1, max: 2.5, default: 1.5, symbol: 'n_2', description: 'Index of refraction of second medium' },
        { id: 'interface_y', name: 'Interface Position', unit: 'm', min: -0.1, max: 0.1, default: 0, symbol: 'y_0', description: 'Y position of interface' },
    ],
    equations: [
        {
            id: 'snells-law',
            name: "Snell's Law",
            latex: 'n_1\\sin\\theta_1 = n_2\\sin\\theta_2',
            description: 'Relationship between angles of incidence and refraction',
        },
        {
            id: 'refracted-angle',
            name: 'Angle of Refraction',
            latex: '\\theta_2 = \\arcsin\\left(\\frac{n_1}{n_2}\\sin\\theta_1\\right)',
            description: 'Angle of refracted ray in second medium',
        },
    ],
    derivatives: (state, _params) => {
        const theta = getState(state, 'theta');
        const v = 0.3; // Ray propagation speed
        return {
            x: v * Math.cos(theta),
            y: v * Math.sin(theta),
            theta: 0,
        };
    },
    derivedState: (state, params) => {
        const theta_i = getState(state, 'theta');
        const n1 = getParam(params, 'n1');
        const n2 = getParam(params, 'n2');
        const sinTheta2 = (n1 / n2) * Math.sin(theta_i);
        const theta_r = Math.abs(sinTheta2) <= 1 ? Math.asin(sinTheta2) : 0;
        const crit_angle = n1 > n2 ? Math.asin(n2 / n1) : 0;
        return {
            theta_incident: theta_i,
            theta_refracted: theta_r,
            critical_angle: crit_angle,
            sin_theta_2: sinTheta2,
            is_tir_possible: n1 > n2 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'tir',
            name: 'Total Internal Reflection',
            check: (_state, params) => {
                const n1 = getParam(params, 'n1');
                const n2 = getParam(params, 'n2');
                return n1 > n2;
            },
            severity: 'warning',
            educationalMessage: 'When light travels from a higher to lower refractive index (n₁ > n₂), total internal reflection can occur if the incident angle exceeds the critical angle.',
        },
        {
            id: 'invalid-refraction',
            name: 'Invalid Refraction Angle',
            check: (state, params) => {
                const theta_i = getState(getStateValues(state), 'theta');
                const n1 = getParam(params, 'n1');
                const n2 = getParam(params, 'n2');
                const sinTheta2 = (n1 / n2) * Math.sin(theta_i);
                return n1 > n2 && Math.abs(sinTheta2) > 1;
            },
            severity: 'warning',
            educationalMessage: 'The angle of incidence exceeds the critical angle! Total internal reflection occurs instead of refraction.',
        },
    ],
    bounds: {
        x: { min: -0.5, max: 0.5 },
        y: { min: -0.1, max: 0.1 },
        theta: { min: -Math.PI / 2, max: Math.PI / 2 },
    },
});
// ============================================================================
// TOTAL INTERNAL REFLECTION
// ============================================================================
export const TotalInternalReflection = createExperimentFromSchema({
    id: 'total-internal-reflection',
    name: 'Total Internal Reflection',
    domain: 'physics',
    description: 'Light ray undergoing total internal reflection at dense-to-rare interface',
    conceptTags: ['Total Internal Reflection', 'Critical Angle', 'TIR', 'Optical Fibers'],
    difficulty: 4,
    stateVariables: [
        { id: 'theta', name: 'Incident Angle', unit: 'rad', initial: 0.8, symbol: '\\theta_i', description: 'Angle from normal' },
        { id: 'path_length', name: 'Ray Path Length', unit: 'm', initial: 0, symbol: 'L' },
    ],
    parameters: [
        { id: 'n_dense', name: 'Dense Medium Index', unit: '', min: 1.1, max: 2.5, default: 1.5, symbol: 'n_1', description: 'Higher refractive index medium' },
        { id: 'n_rare', name: 'Rare Medium Index', unit: '', min: 1, max: 2.4, default: 1, symbol: 'n_2', description: 'Lower refractive index medium' },
    ],
    equations: [
        {
            id: 'critical-angle',
            name: 'Critical Angle',
            latex: '\\theta_c = \\arcsin\\left(\\frac{n_2}{n_1}\\right)',
            description: 'Minimum angle for total internal reflection',
        },
        {
            id: 'tir-condition',
            name: 'TIR Condition',
            latex: '\\theta_i > \\theta_c \\quad \\text{when } n_1 > n_2',
            description: 'Condition for total internal reflection',
        },
    ],
    derivatives: (_state, _params) => {
        return {
            theta: 0,
            path_length: 0.01,
        };
    },
    derivedState: (state, params) => {
        const theta_i = getState(state, 'theta');
        const n1 = getParam(params, 'n_dense');
        const n2 = getParam(params, 'n_rare');
        const theta_c = n1 > n2 ? Math.asin(n2 / n1) : 0;
        const sinTheta_t = (n1 / n2) * Math.sin(theta_i);
        const isTIR = n1 > n2 && theta_i > theta_c;
        return {
            critical_angle: theta_c,
            is_tir: isTIR ? 1 : 0,
            transmitted_angle: !isTIR && Math.abs(sinTheta_t) <= 1 ? Math.asin(sinTheta_t) : 0,
            reflectance: isTIR ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'wrong-direction',
            name: 'Invalid Direction',
            check: (_state, params) => {
                const n1 = getParam(params, 'n_dense');
                const n2 = getParam(params, 'n_rare');
                return n1 <= n2;
            },
            severity: 'error',
            educationalMessage: 'Total internal reflection only occurs when light travels from a denser medium (higher n) to a rarer medium (lower n).',
        },
    ],
    bounds: {
        theta: { min: 0, max: Math.PI / 2 },
    },
});
// ============================================================================
// THIN CONVERGING LENS
// ============================================================================
export const ThinConvergingLens = createExperimentFromSchema({
    id: 'thin-converging-lens',
    name: 'Thin Converging Lens',
    domain: 'physics',
    description: 'Image formation by a thin converging (convex) lens',
    conceptTags: ['Lenses', 'Image Formation', 'Focal Length', 'Magnification'],
    difficulty: 3,
    stateVariables: [
        { id: 'd_o', name: 'Object Distance', unit: 'm', initial: 0.3, symbol: 'd_o', description: 'Distance from object to lens (positive = real object)' },
    ],
    parameters: [
        { id: 'f', name: 'Focal Length', unit: 'm', min: 0.05, max: 1, default: 0.15, symbol: 'f', description: 'Positive focal length for converging lens' },
        { id: 'h_o', name: 'Object Height', unit: 'm', min: 0.001, max: 0.5, default: 0.05, symbol: 'h_o', description: 'Height of object (positive = upright)' },
    ],
    equations: [
        {
            id: 'lens-equation',
            name: 'Thin Lens Equation',
            latex: '\\frac{1}{f} = \\frac{1}{d_o} + \\frac{1}{d_i}',
            description: 'Relationship between focal length and object/image distances',
        },
        {
            id: 'magnification',
            name: 'Magnification',
            latex: 'm = -\\frac{d_i}{d_o} = \\frac{h_i}{h_o}',
            description: 'Ratio of image height to object height',
        },
        {
            id: 'image-distance',
            name: 'Image Distance',
            latex: 'd_i = \\frac{f d_o}{d_o - f}',
            description: 'Distance from lens to image',
        },
    ],
    derivatives: () => ({
        d_o: 0,
    }),
    derivedState: (state, params) => {
        const d_o = getState(state, 'd_o');
        const f = getParam(params, 'f');
        const h_o = getParam(params, 'h_o');
        const d_i = (f * d_o) / (d_o - f);
        const m = -d_i / d_o;
        const h_i = m * h_o;
        return {
            image_distance: d_i,
            magnification: m,
            image_height: h_i,
            is_real: d_i > 0 ? 1 : 0,
            is_virtual: d_i <= 0 ? 1 : 0,
            is_upright: m > 0 ? 1 : 0,
            is_inverted: m < 0 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'object-at-focal',
            name: 'Object at Focal Point',
            check: (state, params) => {
                const d_o = getState(getStateValues(state), 'd_o');
                const f = getParam(params, 'f');
                return Math.abs(d_o - f) < 0.001;
            },
            severity: 'warning',
            educationalMessage: 'When the object is exactly at the focal point, no image forms (rays are parallel and image is at infinity).',
        },
    ],
    bounds: {
        d_o: { min: 0.01, max: 2 },
    },
});
// ============================================================================
// THIN DIVERGING LENS
// ============================================================================
export const ThinDivergingLens = createExperimentFromSchema({
    id: 'thin-diverging-lens',
    name: 'Thin Diverging Lens',
    domain: 'physics',
    description: 'Image formation by a thin diverging (concave) lens',
    conceptTags: ['Lenses', 'Virtual Images', 'Negative Focal Length', 'Diverging Lens'],
    difficulty: 4,
    stateVariables: [
        { id: 'd_o', name: 'Object Distance', unit: 'm', initial: 0.2, symbol: 'd_o' },
    ],
    parameters: [
        { id: 'f', name: 'Focal Length', unit: 'm', min: -1, max: -0.01, default: -0.15, symbol: 'f', description: 'Negative focal length for diverging lens' },
        { id: 'h_o', name: 'Object Height', unit: 'm', min: 0.001, max: 0.5, default: 0.05, symbol: 'h_o' },
    ],
    equations: [
        {
            id: 'diverging-lens-eq',
            name: 'Lens Equation (Diverging)',
            latex: '\\frac{1}{d_i} = \\frac{1}{f} - \\frac{1}{d_o}',
            description: 'Image distance for diverging lens (f is negative)',
        },
        {
            id: 'virtual-image',
            name: 'Virtual Image Property',
            latex: 'd_i < 0, \\quad m > 0',
            description: 'Diverging lenses always form virtual, upright images',
        },
    ],
    derivatives: () => ({
        d_o: 0,
    }),
    derivedState: (state, params) => {
        const d_o = getState(state, 'd_o');
        const f = getParam(params, 'f');
        const h_o = getParam(params, 'h_o');
        const d_i = (f * d_o) / (d_o - f);
        const m = -d_i / d_o;
        const h_i = m * h_o;
        return {
            image_distance: d_i,
            magnification: m,
            image_height: h_i,
            always_virtual: 1,
            always_upright: m > 0 ? 1 : 0,
            image_reduced: Math.abs(m) < 1 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'positive-f',
            name: 'Invalid Focal Length',
            check: (_state, params) => {
                const f = getParam(params, 'f');
                return f > 0;
            },
            severity: 'error',
            educationalMessage: 'A diverging lens must have a negative focal length. Converging lenses have positive focal lengths.',
        },
    ],
    bounds: {
        d_o: { min: 0.01, max: 2 },
    },
});
// ============================================================================
// CONCAVE MIRROR
// ============================================================================
export const ConcaveMirror = createExperimentFromSchema({
    id: 'concave-mirror',
    name: 'Concave Mirror',
    domain: 'physics',
    description: 'Image formation by a concave spherical mirror',
    conceptTags: ['Mirrors', 'Reflection', 'Spherical Aberration', 'Real/Virtual Images'],
    difficulty: 4,
    stateVariables: [
        { id: 'd_o', name: 'Object Distance', unit: 'm', initial: 0.3, symbol: 'd_o', description: 'Distance from object to mirror' },
    ],
    parameters: [
        { id: 'R', name: 'Radius of Curvature', unit: 'm', min: 0.1, max: 2, default: 0.6, symbol: 'R', description: 'Radius of curvature (positive for concave)' },
        { id: 'h_o', name: 'Object Height', unit: 'm', min: 0.001, max: 0.5, default: 0.05, symbol: 'h_o' },
    ],
    equations: [
        {
            id: 'mirror-equation',
            name: 'Mirror Equation',
            latex: '\\frac{1}{f} = \\frac{1}{d_o} + \\frac{1}{d_i}',
            description: 'Relationship between focal length and distances',
        },
        {
            id: 'focal-length',
            name: 'Focal Length',
            latex: 'f = \\frac{R}{2}',
            description: 'Focal length is half the radius of curvature',
        },
        {
            id: 'magnification-mirror',
            name: 'Mirror Magnification',
            latex: 'm = -\\frac{d_i}{d_o}',
            description: 'Negative means inverted image',
        },
    ],
    derivatives: () => ({
        d_o: 0,
    }),
    derivedState: (state, params) => {
        const d_o = getState(state, 'd_o');
        const R = getParam(params, 'R');
        const h_o = getParam(params, 'h_o');
        const f = R / 2;
        const d_i = (f * d_o) / (d_o - f);
        const m = -d_i / d_o;
        const h_i = m * h_o;
        // Case analysis as numeric codes
        // 0=beyond_C, 1=at_C, 2=between_C_F, 3=inside_f, 4=at_f
        let caseType = 0;
        if (Math.abs(d_o - f) < 0.005)
            caseType = 4;
        else if (d_o > R)
            caseType = 0;
        else if (Math.abs(d_o - R) < 0.001)
            caseType = 1;
        else if (d_o < R && d_o > f)
            caseType = 2;
        else
            caseType = 3;
        return {
            focal_length: f,
            image_distance: d_i,
            magnification: m,
            image_height: h_i,
            case_type: caseType,
            beyond_C: d_o > R ? 1 : 0,
            at_C: Math.abs(d_o - R) < 0.001 ? 1 : 0,
            between_C_F: d_o < R && d_o > f ? 1 : 0,
            inside_f: d_o < f ? 1 : 0,
        };
    },
    energyComputation: (_state, params) => {
        // Light energy concentration factor (not physical energy, but useful metric)
        const R = getParam(params, 'R');
        const f = R / 2;
        return {
            kinetic: 0,
            potential: 0,
            thermal: 0,
            total: f,
        };
    },
    failureModes: [
        {
            id: 'object-at-focal',
            name: 'Object at Focal Point',
            check: (state, params) => {
                const d_o = getState(getStateValues(state), 'd_o');
                const R = getParam(params, 'R');
                const f = R / 2;
                return Math.abs(d_o - f) < 0.005;
            },
            severity: 'warning',
            educationalMessage: 'When the object is at the focal point, reflected rays are parallel and no image forms (image at infinity).',
        },
    ],
    bounds: {
        d_o: { min: 0.01, max: 2 },
    },
});
// ============================================================================
// CONVEX MIRROR
// ============================================================================
export const ConvexMirror = createExperimentFromSchema({
    id: 'convex-mirror',
    name: 'Convex Mirror',
    domain: 'physics',
    description: 'Image formation by a convex (diverging) spherical mirror',
    conceptTags: ['Mirrors', 'Virtual Images', 'Convex Mirror', 'Wide Angle View'],
    difficulty: 3,
    stateVariables: [
        { id: 'd_o', name: 'Object Distance', unit: 'm', initial: 0.3, symbol: 'd_o' },
    ],
    parameters: [
        { id: 'R', name: 'Radius of Curvature', unit: 'm', min: -2, max: -0.1, default: -0.6, symbol: 'R', description: 'Negative radius for convex mirror' },
        { id: 'h_o', name: 'Object Height', unit: 'm', min: 0.001, max: 0.5, default: 0.05, symbol: 'h_o' },
    ],
    equations: [
        {
            id: 'convex-focal',
            name: 'Convex Focal Length',
            latex: 'f = \\frac{R}{2} < 0',
            description: 'Focal length is negative (virtual focus behind mirror)',
        },
        {
            id: 'convex-properties',
            name: 'Convex Mirror Properties',
            latex: 'd_i < 0, \\quad m > 0, \\quad |m| < 1',
            description: 'Always virtual, upright, and reduced image',
        },
    ],
    derivatives: () => ({
        d_o: 0,
    }),
    derivedState: (state, params) => {
        const d_o = getState(state, 'd_o');
        const R = getParam(params, 'R');
        const h_o = getParam(params, 'h_o');
        const f = R / 2; // Negative
        const d_i = (f * d_o) / (d_o - f); // Always negative
        const m = -d_i / d_o; // Always positive and < 1
        const h_i = m * h_o;
        return {
            focal_length: f,
            image_distance: d_i,
            magnification: m,
            image_height: h_i,
            always_virtual: 1,
            always_upright: 1,
            always_reduced: Math.abs(m) < 1 ? 1 : 0,
            field_of_view_factor: Math.abs(m),
        };
    },
    failureModes: [
        {
            id: 'invalid-radius',
            name: 'Invalid Radius',
            check: (_state, params) => {
                const R = getParam(params, 'R');
                return R > 0;
            },
            severity: 'error',
            educationalMessage: 'A convex mirror must have a negative radius of curvature. Concave mirrors have positive radius.',
        },
    ],
    bounds: {
        d_o: { min: 0.01, max: 2 },
    },
});
// ============================================================================
// DOUBLE-SLIT INTERFERENCE
// ============================================================================
export const DoubleSlitInterference = createExperimentFromSchema({
    id: 'double-slit-interference',
    name: 'Double-Slit Interference',
    domain: 'physics',
    description: 'Young\'s double-slit interference pattern',
    conceptTags: ['Interference', 'Wave Nature of Light', 'Young\'s Experiment', 'Coherence'],
    difficulty: 5,
    stateVariables: [
        { id: 'y', name: 'Screen Position', unit: 'm', initial: 0, symbol: 'y', description: 'Position on screen from central maximum' },
        { id: 'L_scan', name: 'Scan Position', unit: 'm', initial: 0, symbol: 'y_{scan}', description: 'For scanning across the pattern' },
    ],
    parameters: [
        { id: 'd', name: 'Slit Separation', unit: 'm', min: 1e-6, max: 1e-3, default: 1e-4, symbol: 'd', description: 'Distance between the two slits' },
        { id: 'L', name: 'Screen Distance', unit: 'm', min: 0.1, max: 10, default: 1, symbol: 'L', description: 'Distance from slits to screen' },
        { id: 'lambda', name: 'Wavelength', unit: 'm', min: 380e-9, max: 750e-9, default: 550e-9, symbol: '\\lambda', description: 'Wavelength of light' },
        { id: 'I0', name: 'Max Intensity', unit: 'W/m²', min: 1, max: 1000, default: 100, symbol: 'I_0', description: 'Maximum intensity' },
    ],
    equations: [
        {
            id: 'path-difference',
            name: 'Path Difference',
            latex: '\\Delta r = d\\sin\\theta \\approx \\frac{dy}{L}',
            description: 'Difference in path length from the two slits',
        },
        {
            id: 'intensity',
            name: 'Intensity Pattern',
            latex: 'I(\\theta) = I_0\\cos^2\\left(\\frac{\\pi d\\sin\\theta}{\\lambda}\\right)',
            description: 'Intensity as function of angle',
        },
        {
            id: 'maxima',
            name: 'Bright Fringes',
            latex: 'y_m = \\frac{m\\lambda L}{d}, \\quad m = 0, \\pm 1, \\pm 2, ...',
            description: 'Positions of bright fringes',
        },
        {
            id: 'minima',
            name: 'Dark Fringes',
            latex: 'y_m = \\frac{(m + \\frac{1}{2})\\lambda L}{d}',
            description: 'Positions of dark fringes',
        },
    ],
    derivatives: () => ({
        y: 0,
        L_scan: 0.001,
    }),
    derivedState: (state, params) => {
        const y = getState(state, 'y');
        const d = getParam(params, 'd');
        const L = getParam(params, 'L');
        const lambda = getParam(params, 'lambda');
        const I0 = getParam(params, 'I0');
        const sinTheta = y / Math.sqrt(L * L + y * y);
        const beta = (Math.PI * d * sinTheta) / lambda;
        const intensity = I0 * Math.cos(beta) ** 2;
        // Fringe spacing
        const fringeSpacing = (lambda * L) / d;
        // Order at position y
        const order = (d * y) / (lambda * L);
        return {
            intensity,
            phase_difference: 2 * beta,
            fringe_order: order,
            fringe_spacing: fringeSpacing,
            angular_position: Math.asin(sinTheta),
        };
    },
    energyComputation: (state, _params) => {
        const y = getState(state, 'y');
        // Total energy integrated across screen
        return {
            kinetic: 0,
            potential: 0,
            thermal: 0,
            total: y,
        };
    },
    failureModes: [
        {
            id: 'invalid-wavelength',
            name: 'Invalid Wavelength',
            check: (_state, params) => {
                const lambda = getParam(params, 'lambda');
                return lambda < 380e-9 || lambda > 750e-9;
            },
            severity: 'warning',
            educationalMessage: 'Wavelength is outside the visible range (380-750 nm). The pattern calculation is still valid.',
        },
    ],
    bounds: {
        y: { min: -0.1, max: 0.1 },
    },
});
// ============================================================================
// SINGLE-SLIT DIFFRACTION
// ============================================================================
export const SingleSlitDiffraction = createExperimentFromSchema({
    id: 'single-slit-diffraction',
    name: 'Single-Slit Diffraction',
    domain: 'physics',
    description: 'Diffraction pattern from a single rectangular aperture',
    conceptTags: ['Diffraction', 'Fraunhofer Diffraction', 'Sinc Function', 'Aperture'],
    difficulty: 5,
    stateVariables: [
        { id: 'y', name: 'Screen Position', unit: 'm', initial: 0, symbol: 'y' },
    ],
    parameters: [
        { id: 'a', name: 'Slit Width', unit: 'm', min: 1e-6, max: 1e-3, default: 1e-5, symbol: 'a', description: 'Width of the single slit' },
        { id: 'L', name: 'Screen Distance', unit: 'm', min: 0.1, max: 10, default: 1, symbol: 'L' },
        { id: 'lambda', name: 'Wavelength', unit: 'm', min: 380e-9, max: 750e-9, default: 550e-9, symbol: '\\lambda' },
        { id: 'I0', name: 'Central Intensity', unit: 'W/m²', min: 1, max: 1000, default: 100, symbol: 'I_0' },
    ],
    equations: [
        {
            id: 'diffraction-intensity',
            name: 'Single-Slit Intensity',
            latex: 'I(\\theta) = I_0\\left[\\frac{\\sin(\\alpha)}{\\alpha}\\right]^2',
            description: 'Intensity pattern with sinc squared function',
        },
        {
            id: 'alpha',
            name: 'Diffraction Parameter',
            latex: '\\alpha = \\frac{\\pi a\\sin\\theta}{\\lambda}',
            description: 'Dimensionless diffraction parameter',
        },
        {
            id: 'minima-positions',
            name: 'Diffraction Minima',
            latex: 'a\\sin\\theta = m\\lambda, \\quad m = \\pm 1, \\pm 2, ...',
            description: 'Positions of dark fringes (minima)',
        },
    ],
    derivatives: () => ({
        y: 0,
    }),
    derivedState: (state, params) => {
        const y = getState(state, 'y');
        const a = getParam(params, 'a');
        const L = getParam(params, 'L');
        const lambda = getParam(params, 'lambda');
        const I0 = getParam(params, 'I0');
        const sinTheta = y / Math.sqrt(L * L + y * y);
        const alpha = (Math.PI * a * sinTheta) / lambda;
        let intensity = I0;
        if (Math.abs(alpha) > 1e-10) {
            intensity = I0 * (Math.sin(alpha) / alpha) ** 2;
        }
        // Angular width of central maximum
        const angularWidth = 2 * Math.asin(lambda / a);
        return {
            intensity,
            alpha,
            angular_position: Math.asin(sinTheta),
            central_max_angular_width: angularWidth,
            is_minimum: Math.abs(alpha % Math.PI) < 0.1 && Math.abs(alpha) > 0.1 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'wide-slit',
            name: 'Slit Too Wide',
            check: (_state, params) => {
                const a = getParam(params, 'a');
                const lambda = getParam(params, 'lambda');
                return a > 100 * lambda;
            },
            severity: 'warning',
            educationalMessage: 'When slit width is much larger than wavelength, diffraction effects become negligible and the geometric optics approximation applies.',
        },
    ],
    bounds: {
        y: { min: -0.5, max: 0.5 },
    },
});
// ============================================================================
// DIFFRACTION GRATING
// ============================================================================
export const DiffractionGrating = createExperimentFromSchema({
    id: 'diffraction-grating',
    name: 'Diffraction Grating',
    domain: 'physics',
    description: 'Multi-slit interference from a diffraction grating',
    conceptTags: ['Diffraction Grating', 'Spectroscopy', 'Multi-Slit Interference', 'Sharp Peaks'],
    difficulty: 5,
    stateVariables: [
        { id: 'y', name: 'Screen Position', unit: 'm', initial: 0, symbol: 'y' },
    ],
    parameters: [
        { id: 'd', name: 'Grating Spacing', unit: 'm', min: 1e-7, max: 1e-5, default: 2e-6, symbol: 'd', description: 'Distance between adjacent slits (lines per meter = 1/d)' },
        { id: 'N', name: 'Number of Slits', unit: '', min: 2, max: 10000, default: 500, symbol: 'N', description: 'Total number of illuminated slits' },
        { id: 'L', name: 'Screen Distance', unit: 'm', min: 0.1, max: 10, default: 1, symbol: 'L' },
        { id: 'lambda', name: 'Wavelength', unit: 'm', min: 380e-9, max: 750e-9, default: 550e-9, symbol: '\\lambda' },
        { id: 'I0', name: 'Peak Intensity', unit: 'W/m²', min: 1, max: 1000, default: 100, symbol: 'I_0' },
    ],
    equations: [
        {
            id: 'grating-equation',
            name: 'Grating Equation',
            latex: 'd\\sin\\theta = m\\lambda, \\quad m = 0, \\pm 1, \\pm 2, ...',
            description: 'Condition for principal maxima',
        },
        {
            id: 'grating-intensity',
            name: 'N-Slit Intensity',
            latex: 'I = I_0\\left[\\frac{\\sin(N\\beta)}{\\sin(\\beta)}\\right]^2',
            description: 'Intensity pattern with sharp principal maxima',
        },
        {
            id: 'beta-grating',
            name: 'Phase Parameter',
            latex: '\\beta = \\frac{\\pi d\\sin\\theta}{\\lambda}',
            description: 'Phase difference between adjacent slits',
        },
        {
            id: 'angular-width',
            name: 'Peak Width',
            latex: '\\Delta\\theta = \\frac{\\lambda}{Nd\\cos\\theta}',
            description: 'Angular width of principal maxima',
        },
    ],
    derivatives: () => ({
        y: 0,
    }),
    derivedState: (state, params) => {
        const y = getState(state, 'y');
        const d = getParam(params, 'd');
        const N = getParam(params, 'N');
        const L = getParam(params, 'L');
        const lambda = getParam(params, 'lambda');
        const I0 = getParam(params, 'I0');
        const sinTheta = y / Math.sqrt(L * L + y * y);
        const beta = (Math.PI * d * sinTheta) / lambda;
        let intensity = I0 * N * N;
        if (Math.abs(Math.sin(beta)) > 1e-10) {
            const sincTerm = Math.sin(N * beta) / Math.sin(beta);
            intensity = I0 * (sincTerm * sincTerm);
        }
        // Maximum order visible
        const maxOrder = Math.floor(d / lambda);
        // Check if at a principal maximum
        const order = Math.round((d * sinTheta) / lambda);
        const atPrincipal = Math.abs((d * sinTheta) / lambda - order) < 0.01;
        return {
            intensity,
            beta,
            principal_order: order,
            at_principal_maximum: atPrincipal ? 1 : 0,
            max_visible_order: maxOrder,
            lines_per_meter: 1 / d,
            resolving_power: N * order,
        };
    },
    energyComputation: (_state, params) => {
        const N = getParam(params, 'N');
        // Energy scales with N²
        return {
            kinetic: 0,
            potential: 0,
            thermal: 0,
            total: N * N,
        };
    },
    failureModes: [
        {
            id: 'high-order-not-visible',
            name: 'High Order Not Visible',
            check: (state, params) => {
                const y = getState(getStateValues(state), 'y');
                const L = getParam(params, 'L');
                const d = getParam(params, 'd');
                const lambda = getParam(params, 'lambda');
                const sinTheta = y / Math.sqrt(L * L + y * y);
                const order = Math.round((d * sinTheta) / lambda);
                const maxOrder = Math.floor(d / lambda);
                return Math.abs(order) > maxOrder;
            },
            severity: 'warning',
            educationalMessage: 'This diffraction order is not visible because sin(θ) cannot exceed 1. The maximum visible order is m = ⌊d/λ⌋.',
        },
    ],
    bounds: {
        y: { min: -1, max: 1 },
    },
});
// ============================================================================
// POLARIZATION (MALUS'S LAW)
// ============================================================================
export const MalusLawPolarization = createExperimentFromSchema({
    id: 'malus-law-polarization',
    name: 'Polarization (Malus\'s Law)',
    domain: 'physics',
    description: 'Light transmission through polarizing analyzers (Malus\'s law)',
    conceptTags: ['Polarization', 'Malus\'s Law', 'Polarizers', 'Light Intensity'],
    difficulty: 3,
    stateVariables: [
        { id: 'theta_2', name: 'Analyzer Angle', unit: 'rad', initial: Math.PI / 4, symbol: '\\theta_2', description: 'Angle of analyzer relative to polarizer' },
    ],
    parameters: [
        { id: 'I0', name: 'Initial Intensity', unit: 'W/m²', min: 1, max: 1000, default: 100, symbol: 'I_0', description: 'Unpolarized incident intensity' },
        { id: 'theta_1', name: 'Polarizer Angle', unit: 'rad', min: 0, max: Math.PI, default: 0, symbol: '\\theta_1', description: 'Angle of first polarizer' },
    ],
    equations: [
        {
            id: 'first-polarizer',
            name: 'After First Polarizer',
            latex: 'I_1 = \\frac{I_0}{2}',
            description: 'Unpolarized light becomes 50% polarized',
        },
        {
            id: 'malus-law',
            name: "Malus's Law",
            latex: 'I_2 = I_1\\cos^2(\\theta_2 - \\theta_1)',
            description: 'Intensity through analyzer depends on angle squared',
        },
        {
            id: 'total-transmission',
            name: 'Total Transmission',
            latex: '\\frac{I_2}{I_0} = \\frac{1}{2}\\cos^2(\\Delta\\theta)',
            description: 'Overall transmission through both polarizers',
        },
    ],
    derivatives: () => ({
        theta_2: 0,
    }),
    derivedState: (state, params) => {
        const theta_2 = getState(state, 'theta_2');
        const I0 = getParam(params, 'I0');
        const theta_1 = getParam(params, 'theta_1');
        const I1 = I0 / 2; // After first polarizer
        const deltaTheta = theta_2 - theta_1;
        const I2 = I1 * Math.cos(deltaTheta) ** 2; // After analyzer
        const transmission = I2 / I0;
        return {
            intensity_after_polarizer: I1,
            intensity_after_analyzer: I2,
            transmission_ratio: transmission,
            angle_difference: deltaTheta,
            cos_squared: Math.cos(deltaTheta) ** 2,
            is_crossed: Math.abs(Math.abs(deltaTheta) - Math.PI / 2) < 0.01 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'crossed-polarizers',
            name: 'Crossed Polarizers',
            check: (state, params) => {
                const theta_2 = getState(getStateValues(state), 'theta_2');
                const theta_1 = getParam(params, 'theta_1');
                const deltaTheta = Math.abs(theta_2 - theta_1);
                const modTheta = deltaTheta % Math.PI;
                return Math.abs(modTheta - Math.PI / 2) < 0.05;
            },
            severity: 'warning',
            educationalMessage: 'When polarizers are crossed (90° apart), no light is transmitted! This is the principle behind LCD displays and many optical instruments.',
        },
    ],
    bounds: {
        theta_2: { min: 0, max: Math.PI },
    },
});
//# sourceMappingURL=optics.js.map